import { type GridColDef } from '@mui/x-data-grid-pro'
import { RosterSourceStatus, SortIndexField } from '__generated__/graphql'
import { useMemo } from 'react'
import ProcessStatusPill from 'client/candidate-table-temp/components/ProcessStatusPill'
import { SourceStatusPill } from 'client/candidate-table-temp/components/SourceStatusPill'
import { GetCandidatesForClientPortalSql } from 'client/candidate-table-temp/graphql/candidateSearch.gql'
import ProcessStatusEditCell from 'recruiter/pages/Rosters/RosterProfile/EditCells/ProcessStatusEditCell'
import ProcessStatusEditWithAddToRoster from 'recruiter/pages/Rosters/RosterProfile/EditCells/ProcessStatusEditWithAddToRoster'
import SourceStatusEditCell from 'recruiter/pages/Rosters/RosterProfile/EditCells/SourceStatusEditCell'
import {
    generateRowId,
    type RosterCandidateTableViewId,
} from 'client/candidate-table-temp/components/RosterCandidatesTable/utils'
import { type RosterCandidateTableRowModel } from 'client/candidate-table-temp/hooks/useRosterSearchWithFilters'

type RosterCandidateTableColumnDefinition =
    GridColDef<RosterCandidateTableRowModel> & {
        sortIndexField?: SortIndexField
    }

interface UseAllSpecificColumnDefsParams {
    canEdit: boolean
    currentView: RosterCandidateTableViewId
    rosterId: string
}

export default function useAllSpecificColumnDefs({
    canEdit,
    currentView,
    rosterId,
}: UseAllSpecificColumnDefsParams): Record<
    string,
    RosterCandidateTableColumnDefinition
> {
    const allSpecificColumnDefs = useMemo(() => {
        return {
            processStatus: {
                field: 'processStatus',
                headerName: 'Process Status',
                editable: canEdit ?? false,
                // Disable sorting in the "all candidates" view since process status
                // sorting is only supported when scoped to a specific roster right now.
                sortable: currentView !== 'all',
                sortIndexField:
                    currentView === 'all'
                        ? undefined
                        : SortIndexField.ProcessStatus,
                width: 150,
                renderEditCell: (params) => {
                    if (!canEdit) return '-'
                    if (
                        !params.row.rosterCandidate?.candidateProcessStatus
                            ?.processStatus?.status
                    )
                        return (
                            <ProcessStatusEditWithAddToRoster
                                {...params}
                                canEdit={canEdit ?? false}
                                refetchQueries={[
                                    GetCandidatesForClientPortalSql,
                                ]}
                                candidateId={params.row.candidateId}
                                rosterId={rosterId}
                            />
                        )
                    return (
                        <ProcessStatusEditCell
                            rosterId={rosterId}
                            canEdit={canEdit ?? false}
                            api={params.api}
                            currentProcessStatus={
                                params.row.rosterCandidate
                                    ?.candidateProcessStatus?.processStatus
                            }
                            refetchQueries={[GetCandidatesForClientPortalSql]}
                            rosterCandidateId={
                                params.row.rosterCandidateId || ''
                            }
                            rowId={generateRowId(params.row)}
                        />
                    )
                },
                renderCell: (params) => {
                    if (
                        !params.row.rosterCandidate?.candidateProcessStatus
                            ?.processStatus?.status
                    ) {
                        return (
                            <ProcessStatusEditWithAddToRoster
                                {...params}
                                candidateId={params.row.candidateId}
                                refetchQueries={[
                                    GetCandidatesForClientPortalSql,
                                ]}
                                rosterId={rosterId}
                                onSuccess={(params, rosterCandididates) => {
                                    // when adding a candidate to roster, a row id previously had no rosterCandidateId and now it has both an id and a rosterCandidateId so we need to reselect that row
                                    const selectedRows =
                                        params.api.getSelectedRows()
                                    // deselect the row with the old id
                                    const selectedRowIds = Array.from(
                                        selectedRows.keys()
                                    ).filter(
                                        (id) => id !== params.row.candidateId
                                    )
                                    // select the rows with new ids (only if they were previously selected)
                                    if (
                                        selectedRows.get(params.row.candidateId)
                                    ) {
                                        rosterCandididates.forEach(
                                            ({ id, candidateId }) => {
                                                selectedRowIds.push(
                                                    generateRowId({
                                                        id:
                                                            candidateId ||
                                                            params.row
                                                                .candidateId,
                                                        rosterCandidateId: id,
                                                    })
                                                )
                                            }
                                        )
                                    }
                                    // save the new selected rows to the table
                                    params.api.setRowSelectionModel(
                                        selectedRowIds
                                    )
                                }}
                            />
                        )
                    }
                    return (
                        <ProcessStatusPill
                            processStatusId={
                                params.row.rosterCandidate
                                    ?.candidateProcessStatus?.processStatus?.id
                            }
                        />
                    )
                },
            },
            sourceStatus: {
                field: 'sourceStatus',
                headerName: 'Source Status',
                editable: (canEdit ?? false) && currentView !== 'all',
                sortable: false,
                width: 150,
                renderEditCell: ({ row, api }) => (
                    <SourceStatusEditCell
                        api={api}
                        disabled={!row.rosterCandidate || !(canEdit ?? false)}
                        currentSourceStatus={
                            row.rosterCandidate?.sourceStatus ??
                            RosterSourceStatus.NoStatus
                        }
                        refetchQueries={[GetCandidatesForClientPortalSql]}
                        rosterCandidateId={row.rosterCandidateId || ''}
                        rowId={generateRowId(row)}
                    />
                ),
                renderCell: ({ row }) => (
                    <SourceStatusPill
                        status={row.rosterCandidate?.sourceStatus}
                    />
                ),
            },
        }
    }, [canEdit, currentView, rosterId])

    return allSpecificColumnDefs
}
